/**
 * executionConfigService.ts
 * Carrega configuração de execução (campos, checklist, regras) das tabelas v2.
 * Fallback: metadata JSON embutida na task (compatibilidade).
 */
import { supabase } from '../../../lib/supabaseClient';
import type { ExecutionField, ExecutionChecklistItem, StageRule, ExecutionConfig } from '../../../types/card';
import type { BlueprintFormField, BlueprintChecklistItem } from '../../../types/processBlueprint';

// ============================================================================
// TIPOS DE RETORNO
// ============================================================================

export interface StageExecutionConfig {
  instruction: string | null;
  completionType: 'simple_complete' | 'approve_reject';
  fields: ExecutionField[];
  checklist: ExecutionChecklistItem[];
  rules: StageRule[];
  slaHours: number | null;
  requireAttachment: boolean;
  requireJustification: boolean;
  requirePhoto: boolean;
  requireAllChecklist: boolean;
  requireChecklistOrder: boolean;
  /** Indica se veio das tabelas v2 (true) ou do metadata fallback (false) */
  fromV2Tables: boolean;
}

// ============================================================================
// LOAD — Configuração completa da etapa
// ============================================================================

/**
 * Carrega configuração de execução para uma etapa.
 * Prioridade: tabelas v2 → metadata da task (fallback).
 */
export async function loadExecutionConfig(
  stageId: string,
  organizationId: string,
  metadataFallback?: {
    instruction?: string | null;
    completionType?: 'simple_complete' | 'approve_reject';
    formFields?: BlueprintFormField[];
    checklist?: BlueprintChecklistItem[];
    executionConfig?: Partial<ExecutionConfig> | null;
  },
): Promise<StageExecutionConfig> {
  // Tentar carregar das tabelas v2
  const [fields, checklist, rules, stageMeta] = await Promise.all([
    loadFieldsFromV2(stageId, organizationId),
    loadChecklistFromV2(stageId, organizationId),
    loadRulesFromV2(stageId, organizationId),
    loadStageMetadata(stageId),
  ]);

  const hasV2Data = fields.length > 0 || checklist.length > 0 || rules.length > 0;

  // Resolve instruction: task metadata → stage metadata → executionConfig (stage)
  const stageInstruction = stageMeta?.instruction as string | null ?? null;
  const stageExecConfig = stageMeta?.executionConfig as Partial<ExecutionConfig> | null ?? null;

  if (hasV2Data) {
    return {
      instruction: metadataFallback?.instruction ?? stageInstruction ?? stageExecConfig?.instruction ?? null,
      completionType: metadataFallback?.completionType ?? 'simple_complete',
      fields,
      checklist,
      rules,
      slaHours: metadataFallback?.executionConfig?.slaHours ?? stageExecConfig?.slaHours ?? null,
      requireAttachment: metadataFallback?.executionConfig?.requireAttachment ?? stageExecConfig?.requireAttachment ?? false,
      requireJustification: metadataFallback?.executionConfig?.requireJustification ?? stageExecConfig?.requireJustification ?? false,
      requirePhoto: metadataFallback?.executionConfig?.requirePhoto ?? stageExecConfig?.requirePhoto ?? false,
      requireAllChecklist: metadataFallback?.executionConfig?.requireAllChecklist ?? stageExecConfig?.requireAllChecklist ?? true,
      requireChecklistOrder: metadataFallback?.executionConfig?.requireChecklistOrder ?? stageExecConfig?.requireChecklistOrder ?? false,
      fromV2Tables: true,
    };
  }

  // Fallback: usar metadata da task → stage metadata
  const fb = metadataFallback;
  const ec = fb?.executionConfig ?? stageExecConfig;
  return {
    instruction: fb?.instruction ?? stageInstruction ?? ec?.instruction ?? null,
    completionType: fb?.completionType ?? ec?.actionType ?? 'simple_complete',
    fields: ec?.fields ?? mapBlueprintFieldsToExecution(fb?.formFields ?? []),
    checklist: ec?.checklist ?? mapBlueprintChecklistToExecution(fb?.checklist ?? []),
    rules: ec?.rules ?? [],
    slaHours: ec?.slaHours ?? null,
    requireAttachment: ec?.requireAttachment ?? false,
    requireJustification: ec?.requireJustification ?? false,
    requirePhoto: ec?.requirePhoto ?? false,
    requireAllChecklist: ec?.requireAllChecklist ?? true,
    requireChecklistOrder: ec?.requireChecklistOrder ?? false,
    fromV2Tables: false,
  };
}

// ============================================================================
// QUERIES — Tabelas V2
// ============================================================================

async function loadStageMetadata(stageId: string): Promise<Record<string, unknown> | null> {
  const { data, error } = await supabase
    .from('workflow_stages_v9')
    .select('metadata')
    .eq('id', stageId)
    .maybeSingle();
  if (error || !data) return null;
  return (data.metadata as Record<string, unknown>) ?? null;
}

async function loadFieldsFromV2(stageId: string, orgId: string): Promise<ExecutionField[]> {
  const { data, error } = await supabase
    .from('workflow_fields_v2')
    .select('id, label, type, required, default_value, validation_rules, visibility_rules, placeholder, group_name, visible, visibility_condition, editable_by, visible_by, description, order_index')
    .eq('workflow_stage_id', stageId)
    .eq('organization_id', orgId)
    .order('order_index', { ascending: true });

  if (error || !data) return [];

  // Carregar opções de select em batch
  const fieldIds = data.filter(f => f.type === 'select').map(f => f.id);
  let optionsMap: Record<string, { value: string; label: string }[]> = {};

  if (fieldIds.length > 0) {
    const { data: opts } = await supabase
      .from('workflow_field_options')
      .select('field_id, value, label, order_index')
      .in('field_id', fieldIds)
      .eq('organization_id', orgId)
      .order('order_index', { ascending: true });

    if (opts) {
      optionsMap = opts.reduce((acc, o) => {
        (acc[o.field_id] ??= []).push({ value: o.value, label: o.label });
        return acc;
      }, {} as Record<string, { value: string; label: string }[]>);
    }
  }

  return data.map((row) => ({
    id: row.id,
    label: row.label,
    type: row.type as ExecutionField['type'],
    required: row.required,
    defaultValue: row.default_value ?? undefined,
    placeholder: row.placeholder ?? undefined,
    description: row.description ?? undefined,
    groupName: row.group_name ?? undefined,
    visible: row.visible ?? true,
    validation: row.validation_rules && Object.keys(row.validation_rules).length > 0
      ? row.validation_rules
      : undefined,
    visibilityRule: row.visibility_condition ?? (row.visibility_rules && Object.keys(row.visibility_rules).length > 0 ? row.visibility_rules : undefined),
    options: optionsMap[row.id] ?? undefined,
    editableBy: row.editable_by?.length > 0 ? row.editable_by : undefined,
    visibleBy: row.visible_by?.length > 0 ? row.visible_by : undefined,
  }));
}

async function loadChecklistFromV2(stageId: string, orgId: string): Promise<ExecutionChecklistItem[]> {
  const { data, error } = await supabase
    .from('workflow_checklist_items_v2')
    .select('id, label, required, requires_attachment, requires_field, order_index')
    .eq('workflow_stage_id', stageId)
    .eq('organization_id', orgId)
    .order('order_index', { ascending: true });

  if (error || !data) return [];

  return data.map((row) => ({
    id: row.id,
    label: row.label,
    required: row.required,
    orderIndex: row.order_index,
    requiresAttachment: row.requires_attachment ?? undefined,
    requiresField: row.requires_field ?? undefined,
  }));
}

async function loadRulesFromV2(stageId: string, orgId: string): Promise<StageRule[]> {
  const { data, error } = await supabase
    .from('workflow_stage_rules_v2')
    .select('id, rule_type, condition, action, message, type, is_active, order_index')
    .eq('workflow_stage_id', stageId)
    .eq('organization_id', orgId)
    .eq('is_active', true)
    .order('order_index', { ascending: true });

  if (error || !data) return [];

  return data.map((row) => ({
    id: row.id,
    condition: row.condition as StageRule['condition'],
    action: (row.rule_type ?? row.action) as StageRule['action'],
    message: row.message ?? undefined,
    type: (row.type as 'error' | 'warning') ?? 'error',
  }));
}

// ============================================================================
// MAPPERS — Blueprint → ExecutionField (fallback)
// ============================================================================

function mapBlueprintFieldsToExecution(fields: BlueprintFormField[]): ExecutionField[] {
  return fields.map((f) => ({
    id: f.id,
    label: f.label,
    type: f.type as ExecutionField['type'],
    required: f.required ?? false,
    defaultValue: f.defaultValue,
    options: f.options?.map((o) => (typeof o === 'string' ? { value: o, label: o } : o)),
    validation: f.validation,
    visibilityRule: f.visibilityRule as ExecutionField['visibilityRule'],
  }));
}

function mapBlueprintChecklistToExecution(items: BlueprintChecklistItem[]): ExecutionChecklistItem[] {
  return items.map((item, idx) => ({
    id: item.id,
    label: item.label,
    required: item.required ?? true,
    orderIndex: idx,
  }));
}
